import styled from "styled-components";
import { COLOR_SYSTEM } from "../../../../design/design-system";

/**
 * 본문 인라인 마커 버튼 공통 기반 — spec 설계 결정 13(V2안).
 *
 * HospitalButton·PrescriptionButton이 공유하는 유일한 시각 기반이다. 각 컴포넌트가 인라인 스타일
 * 객체를 따로 들면 다시 갈라지므로(1차 실측 결함) 여기 하나로 모은다. `atoms/Button`(폼 버튼 규격:
 * inline-flex·height 32px·overflow hidden)에서 떼어내 **본문 글자와 픽셀 단위로 같은 자리**에 놓는다.
 *
 * 규격(최종 — 본문 마커는 선택 여부와 무관하게 항상 같은 모습. 배경은 어느 상태에도 없다):
 *  - 기본: font/color/line-height inherit · 패딩·마진·라운드 0 · display inline ·
 *          무채색 실선 밑줄(Gray 400, 1px, offset 1px). border-bottom 금지(줄바꿈 조각마다 그려져 행 높이를 민다).
 *  - hover: 글자색 Skyblue 500 + 밑줄 currentColor(선 모양·두께·offset 불변).
 *
 * "선택됨"은 본문에 표시하지 않는다(ref 버튼과 동일 규칙 · spec 설계 결정 13, 2026-09-01). 마커가 한
 * 보고서에 약 149회 등장하는데 그중 하나만 색·배경이 바뀌면 읽는 흐름이 끊긴다 — 선택 상태는 좌측
 * 처방 패널에만 남는다. 밑줄은 정체 표시로 고정이고 모양이 변하지 않는다(solid 1px·offset 1px).
 * 렌더 태그는 `button`을 유지한다 — 편집 진입 DOM 추출이 `button[data-hospital-alias]`를 찾는다.
 * 색은 COLOR_SYSTEM 토큰만.
 */
export const InlineMarkerButton = styled.button`
  display: inline;
  font: inherit;
  line-height: inherit;
  color: inherit;
  border: none;
  border-radius: 0;
  margin: 0;
  padding: 0;
  cursor: pointer;
  transition: color 0.12s ease;

  /* 버튼 UA 기본 외형을 명시적으로 죽인다. 선언이 없으면 투명이 아니라 브라우저 기본 버튼 배경
     (buttonface, 크롬·사파리에서 연회색)이 적용돼 검은 의사 서한 badge 위에서 회색 상자가 된다.
     background-color longhand까지 명시해 shorthand 확장에 의존하지 않고 확실히 투명으로 고정한다.
     appearance:none은 사파리·크롬의 버튼 기본 외형(코너·인셋) 잔재를 차단한다. */
  -webkit-appearance: none;
  appearance: none;
  background: none;
  background-color: transparent;

  /* 무채색 실선 밑줄(Gray 400, 1px, offset 1px) — 선택 여부와 무관하게 항상 동일. 색 longhand는
     shorthand 뒤에 둬야 shorthand 리셋을 이긴다. */
  text-decoration: underline solid;
  text-decoration-color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  text-decoration-thickness: 1px;
  text-underline-offset: 1px;

  /* hover: 글자색만 Skyblue 500으로, 밑줄은 currentColor로 함께 물든다. shorthand가 두께를 auto로
     리셋하므로 thickness·offset을 다시 명시해 base와 1px·1px 동일 — hover 진입 시 밑줄이 튀지 않는다. */
  &:hover {
    color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
    text-decoration: underline solid;
    text-decoration-color: currentColor;
    text-decoration-thickness: 1px;
    text-underline-offset: 1px;
  }
`;

export default InlineMarkerButton;
